import type { CapabilityOffering } from "./types";

export const CAPABILITY_OFFERINGS: CapabilityOffering[] = [
  {
    kind: "capability",
    id: "ux-strategy-and-design",
    title: "UX Strategy & Design",
    tagline: "Research, structure, and interfaces that fit your users and business.",
    description:
      "How I help when you need clarity before pixels — user research, information architecture, wireframes, usability testing, and design systems. Priced hourly or as a scoped project.",
    includes: [
      "User research & analysis",
      "Information architecture",
      "Wireframing & prototyping",
      "Usability testing",
      "Design systems",
    ],
    price: "From €36 / hour",
    timeline: "Project or hourly",
    processSteps: [
      {
        title: "Understand the problem",
        description: "Stakeholder interviews, analytics, and defining what success means.",
      },
      {
        title: "Research & map",
        description: "Users, journeys, and content structure before UI detail.",
      },
      {
        title: "Explore solutions",
        description: "Wireframes and prototypes to test ideas cheaply.",
      },
      {
        title: "Validate",
        description: "Usability tests or structured reviews with real users.",
      },
      {
        title: "Document & hand off",
        description: "Specs, patterns, and a roadmap your team can build from.",
      },
    ],
    portfolioTypes: ["product-design"],
    exampleProjectIds: ["alloy", "toonect"],
    featured: true,
  },
  {
    kind: "capability",
    id: "product-development",
    title: "Product Development",
    tagline: "Design and front-end delivery together — from concept to shipped product.",
    description:
      "End-to-end delivery when you want one partner for UX, UI, and implementation. Typical for SaaS, dashboards, and marketing sites that need to go live, not sit in Figma.",
    includes: [
      "Product strategy & UX",
      "UI design & design systems",
      "React / modern front-end",
      "Iterative releases",
    ],
    price: "Project quote",
    timeline: "8–12 weeks or ongoing",
    processSteps: [
      {
        title: "Scope & plan",
        description: "MVP definition, milestones, and how we work week to week.",
      },
      {
        title: "Design sprints",
        description: "Flows and UI in short cycles with your feedback built in.",
      },
      {
        title: "Build",
        description: "Front-end implementation aligned to the approved design system.",
      },
      {
        title: "Test & refine",
        description: "QA, accessibility, and fixes from real usage.",
      },
      {
        title: "Ship & support",
        description: "Launch, monitoring, and optional ongoing improvements.",
      },
    ],
    portfolioTypes: ["product-design", "web-design"],
    exampleProjectIds: ["intobridge", "alloy", "slovakfinance"],
    featured: true,
  },
  {
    kind: "capability",
    id: "design-only",
    title: "Design Only",
    tagline: "UX and UI deliverables for your in-house or agency dev team.",
    description:
      "When you already have engineering covered and need strong design — research, flows, high-fidelity UI, prototypes, and documentation without me writing production code.",
    includes: [
      "UX flows & wireframes",
      "High-fidelity UI",
      "Interactive prototypes",
      "Dev-ready specs & assets",
    ],
    price: "From €36 / hour",
    timeline: "Flexible",
    processSteps: [
      {
        title: "Align with your team",
        description: "How design slots into your sprint cycle and tooling.",
      },
      {
        title: "Design exploration",
        description: "Directions and feedback loops with stakeholders.",
      },
      {
        title: "Production UI",
        description: "Final screens, states, and component definitions.",
      },
      {
        title: "Prototype",
        description: "Clickable flows for review or user testing.",
      },
      {
        title: "Handoff",
        description: "Figma, redlines, and walkthrough with developers.",
      },
    ],
    portfolioTypes: ["product-design", "web-design", "mobile-design"],
    exampleProjectIds: ["alloy", "elisabeth"],
  },
  {
    kind: "capability",
    id: "front-end-development",
    title: "Front-end Development",
    tagline: "React, TypeScript, and modern sites from approved designs.",
    description:
      "Implementation-only engagement when designs exist — accessible, performant front-end that matches specs and integrates with your API or CMS.",
    includes: [
      "React / TypeScript builds",
      "Responsive implementation",
      "Performance & accessibility",
      "CMS / API integration",
    ],
    price: "From €36 / hour",
    timeline: "Project-based",
    processSteps: [
      {
        title: "Technical review",
        description: "Stack, repo, and how designs map to components.",
      },
      {
        title: "Component build",
        description: "Reusable UI aligned to your design tokens.",
      },
      {
        title: "Page assembly",
        description: "Wire content, routing, and responsive behaviour.",
      },
      {
        title: "Integration",
        description: "Hooks to backend, CMS, or third-party services.",
      },
      {
        title: "QA & deploy",
        description: "Cross-browser checks and support for go-live.",
      },
    ],
    portfolioTypes: ["web-design"],
    exampleProjectIds: ["intobridge", "map4nomads"],
  },
  {
    kind: "capability",
    id: "design-sprint",
    title: "Design Sprint",
    tagline: "One week to validate an idea with a prototype and tested plan.",
    description:
      "A focused Design Sprint — problem framing, solution sketching, prototype, and user testing compressed into five days so you can decide what to build next.",
    includes: [
      "Problem definition",
      "Solution ideation",
      "Rapid prototyping",
      "User testing",
      "Implementation roadmap",
    ],
    price: "From €1,400",
    timeline: "1 week",
    processSteps: [
      {
        title: "Monday — Map",
        description: "Long-term goal, map the challenge, and pick a target.",
      },
      {
        title: "Tuesday — Sketch",
        description: "Individual solutions, then a shared direction.",
      },
      {
        title: "Wednesday — Decide",
        description: "Storyboard and choose what to prototype.",
      },
      {
        title: "Thursday — Prototype",
        description: "Realistic facade ready for testing.",
      },
      {
        title: "Friday — Test",
        description: "User interviews and a clear go / no-go recommendation.",
      },
    ],
    portfolioTypes: ["product-design"],
    featured: true,
  },
  {
    kind: "capability",
    id: "maintenance-support",
    title: "Maintenance & Support",
    tagline: "Ongoing care for sites and apps already live.",
    description:
      "Bug fixes, content updates, small UX improvements, and release help when you need a steady design/dev hand without a full project engagement.",
    includes: [
      "Bug fixes & tweaks",
      "Content & CMS updates",
      "Performance & UX polish",
      "Release coordination",
    ],
    price: "€36 / hour or monthly retainer",
    timeline: "Ongoing",
    processSteps: [
      {
        title: "Onboarding",
        description: "Access, stack, and how you prefer to request work.",
      },
      {
        title: "Triage",
        description: "Prioritise fixes vs improvements vs content.",
      },
      {
        title: "Execute",
        description: "Small batches of changes with clear changelog.",
      },
      {
        title: "Review",
        description: "Staging check before production deploy.",
      },
      {
        title: "Report",
        description: "Monthly summary of time spent and what shipped.",
      },
    ],
    portfolioTypes: ["web-design", "product-design"],
  },
];

export function getCapabilityById(id: string): CapabilityOffering | undefined {
  return CAPABILITY_OFFERINGS.find((s) => s.id === id);
}
